<template>
  <div class="bottom-box" :class="wrapClass">
    <div class="left-wrap">
      <woot-button
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TOOLS')"
        :title="$t('CONVERSATION.REPLYBOX.TOOLS')"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleOptionsStatus"
      >
        <proto-icon name="add" />
      </woot-button>
      <transition name="menu-slide">
        <div
          v-if="show"
          v-on-clickaway="onClickAway"
          class="options-menu dropdown-pane"
          :class="{ 'dropdown-pane--open': show }"
        >
          <woot-dropdown-menu>
            <woot-dropdown-item>
              <woot-button
                v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.CONTACT')"
                :title="$t('CONVERSATION.REPLYBOX.CONTACT')"
                color-scheme="secondary"
                variant="smooth"
                size="small"
                :on-send="onSend"
                :inbox="inbox"
                @click="showModalNewConversation"
              >
                <proto-icon name="person" />
              </woot-button>
            </woot-dropdown-item>

            <woot-dropdown-item>
              <file-upload
                ref="upload"
                v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                :accept="allowedFileTypes"
                :multiple="enableMultipleFileUpload"
                :drop="true"
                :drop-directory="false"
                @input-filter="inputFilter"
                @input-file="onUpload"
              >
                <woot-button
                  class-names="button--upload"
                  :title="$t('CONVERSATION.REPLYBOX.TIP_ATTACH_ICON')"
                  color-scheme="secondary"
                  variant="smooth"
                  size="small"
                >
                  <proto-icon name="attach" />
                </woot-button>
              </file-upload>
            </woot-dropdown-item>

            <woot-dropdown-item>
              <woot-button
                v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.LOCATION')"
                :title="$t('CONVERSATION.REPLYBOX.LOCATION')"
                color-scheme="secondary"
                variant="smooth"
                size="small"
                @click="toggleLocationModal"
              >
                <proto-icon name="location" />
              </woot-button>
            </woot-dropdown-item>
          </woot-dropdown-menu>
        </div>
      </transition>
      <woot-button
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        :title="$t('CONVERSATION.REPLYBOX.TIP_EMOJI_ICON')"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleEmojiPicker"
      >
        <proto-icon name="emoji" />
      </woot-button>
      <woot-button
        v-if="showAudioRecorderButton"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_AUDIORECORDER_ICON')"
        :color-scheme="!isRecordingAudio ? 'secondary' : 'alert'"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorder"
      >
        <proto-icon :name="!isRecordingAudio ? 'microphone' : 'microphone-off'" />
      </woot-button>
      <woot-button
        v-if="showEditorToggle"
        v-tooltip.top-end="$t('CONVERSATION.REPLYBOX.TIP_FORMAT_ICON')"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="$emit('toggle-editor')"
      >
        <proto-icon name="quote" />
      </woot-button>
      <woot-button
        v-if="showAudioPlayStopButton"
        :icon="audioRecorderPlayStopIcon"
        emoji="🎤"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="toggleAudioRecorderPlayPause"
      >
        <span>{{ recordingAudioDurationText }}</span>
      </woot-button>
      <woot-button
        v-if="showMessageSignatureButton"
        v-tooltip.top-end="signatureToggleTooltip"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        :title="signatureToggleTooltip"
        @click="toggleMessageSignature"
      >
        <proto-icon name="signature" />
      </woot-button>
      <woot-button
        v-if="hasWhatsappTemplates"
        v-tooltip.top-end="'Whatsapp Templates'"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        :title="'Whatsapp Templates'"
        @click="$emit('selectWhatsappTemplate')"
      >
        <proto-icon name="whatsapp" />
      </woot-button>
      <video-call-button
        v-if="(isAWebWidgetInbox || isAPIInbox) && !isOnPrivateNote"
        :conversation-id="conversationId"
      />
      <woot-button
        v-tooltip.top-end="$t('CONVERSATION.CATALOG_PRODUCT.TIP_FORMAT_ICON')"
        color-scheme="secondary"
        variant="smooth"
        size="small"
        @click="handleModalCatalogProduct"
      >
        🛒
      </woot-button>

    <woot-button
      v-tooltip.top-end="memoTooltip"
      :color-scheme="memoButtonColor"
      variant="smooth"
      size="small"
      @click="toggleMemoModal"
    >
      <proto-icon name="memo-pad" />
    </woot-button>

      <AIAssistanceButton
        :conversation-id="conversationId"
        :is-private-note="isOnPrivateNote"
        :message="message"
        @replace-text="replaceText"
      />
      <transition name="modal-fade">
        <div
          v-show="$refs.upload && $refs.upload.dropActive"
          class="modal-mask"
        >
          <fluent-icon icon="cloud-backup" />
          <h4 class="page-sub-title">
            {{ $t('CONVERSATION.REPLYBOX.DRAG_DROP') }}
          </h4>
        </div>
      </transition>
    </div>
    <div class="right-wrap">
      <woot-button
        size="small"
        class="bg-primary"
        :class-names="buttonClass"
        :is-disabled="isSendDisabled"
        @click="onSend"
      >
        {{ sendButtonText }}
      </woot-button>
    </div>

    <woot-modal
      :show.sync="toggleContactsModal"
      :on-close="hideContactsModal"
      size="modal-xl"
    >
      <div class="column content-box">
        <div class="row">
          <contacts-view
            :conversation-id="conversationId"
            :on-send="onSend"
            :message="message"
            :toggle-contact="toggleContact"
            @toggle-contacts-view="onToggleContactsModal"
          />
        </div>
      </div>
    </woot-modal>
    <woot-modal :show.sync="showLocationModal" :on-close="onClose">
      <div class="google-autocomplete-input">
        <h2>{{ $t('CONVERSATION.REPLYBOX.LOCATION') }}</h2>
        <vue-google-autocomplete
          id="map"
          ref="address"
          classname="form-control"
          class="google-autocomplete"
          :placeholder="$t('CONVERSATION.REPLYBOX.LOCATION')"
          types=""
          @placechanged="selectLocation"
        />
        <br />
      </div>
    </woot-modal>
    <catalog-product-modal
      :show-modal="showModalCatalogProduct"
      @on-close="handleModalCatalogProduct"
      @handle-send-message="handleSendMessage"
    />
    <memo-modal
      :show="showMemoModal"
      :conversation-id="conversationId"
      :current-memo="currentMemoMessage"
      :memo-sent="isMemoSent"
      @close="toggleMemoModal"
      @memo-saved="onMemoSaved"
      @memo-removed="onMemoRemoved"
    />
  </div>
</template>

<script>
import FileUpload from 'vue-upload-component';
import VueGoogleAutocomplete from 'vue-google-autocomplete';
import * as ActiveStorage from 'activestorage';
import { hasPressedAltAndAKey } from 'shared/helpers/KeyboardHelpers';
import eventListenerMixins from 'shared/mixins/eventListenerMixins';
import uiSettingsMixin from 'dashboard/mixins/uiSettings';
import inboxMixin from 'shared/mixins/inboxMixin';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import {
  ALLOWED_FILE_TYPES,
  ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP,
} from 'shared/constants/messages';
import VideoCallButton from '../VideoCallButton';
import AIAssistanceButton from '../AIAssistanceButton.vue';
import { REPLY_EDITOR_MODES } from './constants';
import { mapGetters } from 'vuex';
import WootDropdownItem from 'shared/components/ui/dropdown/DropdownItem';
import WootDropdownMenu from 'shared/components/ui/dropdown/DropdownMenu';
import { mixin as clickaway } from 'vue-clickaway';
import ContactsView from '../../../routes/dashboard/contacts/components/ContactsView.vue';
import CatalogProductModal from './CatalogProductModal.vue';
import MemoModal from './MemoModal.vue';
import WootButton from '../../ui/WootButton.vue';
import ProtoIcon from 'shared/components/ProtoIcon.vue';

export default {
  name: 'ReplyBottomPanel',
  components: {
    FileUpload,
    VideoCallButton,
    AIAssistanceButton,
    WootDropdownMenu,
    WootDropdownItem,
    VueGoogleAutocomplete,
    ContactsView,
    CatalogProductModal,
    MemoModal,
    WootButton,
    ProtoIcon,
  },
  mixins: [clickaway, eventListenerMixins, uiSettingsMixin, inboxMixin],
  props: {
    mode: {
      type: String,
      default: REPLY_EDITOR_MODES.REPLY,
    },
    onSend: {
      type: Function,
      default: () => {},
    },
    sendButtonText: {
      type: String,
      default: '',
    },
    recordingAudioDurationText: {
      type: String,
      default: '',
    },
    inbox: {
      type: Object,
      default: () => ({}),
    },
    showFileUpload: {
      type: Boolean,
      default: false,
    },
    showAudioRecorder: {
      type: Boolean,
      default: false,
    },
    onFileUpload: {
      type: Function,
      default: () => {},
    },
    showEmojiPicker: {
      type: Boolean,
      default: false,
    },
    toggleEmojiPicker: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorder: {
      type: Function,
      default: () => {},
    },
    toggleAudioRecorderPlayPause: {
      type: Function,
      default: () => {},
    },
    toggleLocation: {
      type: Function,
      default: () => {},
    },
    toggleContact: {
      type: Function,
      default: () => {},
    },
    isRecordingAudio: {
      type: Boolean,
      default: false,
    },
    recordingAudioState: {
      type: String,
      default: '',
    },
    isSendDisabled: {
      type: Boolean,
      default: false,
    },
    showEditorToggle: {
      type: Boolean,
      default: false,
    },
    isOnPrivateNote: {
      type: Boolean,
      default: false,
    },
    enableMultipleFileUpload: {
      type: Boolean,
      default: true,
    },
    hasWhatsappTemplates: {
      type: Boolean,
      default: false,
    },
    conversationId: {
      type: Number,
      required: true,
    },
    message: {
      type: String,
      default: '',
    },
    showLocationModal: {
      type: Boolean,
      default: false,
    },
    toggleContactsModal: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showModalCatalogProduct: false,
      showMemoModal: false,
      memoMessage: '',
      memoSent: false,
      show: false,
      mediaRules: {
        audio: {
          types: [
            'audio/aac',
            'audio/mp4',
            'audio/amr',
            'audio/mpeg',
            'audio/ogg; codecs=opus',
          ],
          maxSize: 16 * 1024 * 1024, // 16MB
          errorMessage: (filename, max) => {
            bus.$emit(
              'newToastMessage',
              this.$t('CONVERSATION.REPLYBOX.AUDIO_ERROR', {
                filename,
                max,
              })
            );
          },
        },
        image: {
          types: ['image/jpeg', 'image/png','image/heic','image/heif'],
          maxSize: 5 * 1024 * 1024, // 5MB
          errorMessage: (filename, max) => {
            bus.$emit(
              'newToastMessage',
              this.$t('CONVERSATION.REPLYBOX.IMAGE_ERROR', {
                filename,
                max,
              })
            );
          },
        },
        sticker: {
          types: ['image/webp'],
          maxSize: 100 * 1024, // 100KB
          errorMessage: (filename, max) => {
            bus.$emit(
              'newToastMessage',
              this.$t('CONVERSATION.REPLYBOX.STICKER_ERROR', {
                filename,
                max,
              })
            );
          },
        },
        video: {
          types: ['video/mp4', 'video/3gpp'],
          maxSize: 16 * 1024 * 1024, // 16MB
          errorMessage: (filename, max) => {
            bus.$emit(
              'newToastMessage',
              this.$t('CONVERSATION.REPLYBOX.VIDEO_ERROR', {
                filename,
                max,
              })
            );
          },
        },
        document: {
          types: ['*/*'],
          maxSize: 100 * 1024 * 1024, // 100MB
          errorMessage: (filename, max) => {
            bus.$emit(
              'newToastMessage',
              this.$t('CONVERSATION.REPLYBOX.DOCUMENT_ERROR', {
                filename,
                max,
              })
            );
          },
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      accountId: 'getCurrentAccountId',
      isFeatureEnabledonAccount: 'accounts/isFeatureEnabledonAccount',
      currentChat: 'getSelectedChat',
    }),
    isNote() {
      return this.mode === REPLY_EDITOR_MODES.NOTE;
    },
    wrapClass() {
      return {
        'is-note-mode': this.isNote,
      };
    },
    buttonClass() {
      return {
        warning: this.isNote,
      };
    },
    showAttachButton() {
      return this.showFileUpload || this.isNote;
    },
    showAudioRecorderButton() {
      // Disable audio recorder for safari browser as recording is not supported
      const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(
        navigator.userAgent
      );

      return (
        this.isFeatureEnabledonAccount(
          this.accountId,
          FEATURE_FLAGS.VOICE_RECORDER
        ) &&
        this.showAudioRecorder &&
        !isSafari
      );
    },
    showAudioPlayStopButton() {
      return this.showAudioRecorder && this.isRecordingAudio;
    },
    allowedFileTypes() {
      if (this.isATwilioWhatsAppChannel) {
        return ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP;
      }
      return ALLOWED_FILE_TYPES;
    },
    audioRecorderPlayStopIcon() {
      switch (this.recordingAudioState) {
        // playing paused recording stopped inactive destroyed
        case 'playing':
          return 'microphone-pause';
        case 'paused':
          return 'microphone-play';
        case 'stopped':
          return 'microphone-play';
        default:
          return 'microphone-stop';
      }
    },
    showMessageSignatureButton() {
      return !this.isOnPrivateNote && this.isAnEmailChannel;
    },
    sendWithSignature() {
      const { send_with_signature: isEnabled } = this.uiSettings;
      return isEnabled;
    },
    signatureToggleTooltip() {
      return this.sendWithSignature
        ? this.$t('CONVERSATION.FOOTER.DISABLE_SIGN_TOOLTIP')
        : this.$t('CONVERSATION.FOOTER.ENABLE_SIGN_TOOLTIP');
    },
    filteredItems() {
      return this.items.filter(item =>
        item.toLowerCase().includes(this.inputValue.toLowerCase())
      );
    },
    currentMemoMessage() {
      return this.memoMessage || this.currentChat?.additional_attributes?.memo_message || '';
    },
    isMemoSent() {
      return this.memoSent || this.currentChat?.additional_attributes?.memo_sent === true;
    },
    hasMemo() {
      return this.currentMemoMessage && this.currentMemoMessage.trim().length > 0;
    },
    memoButtonColor() {
      if (this.isMemoSent) return 'success';
      if (this.hasMemo) return 'warning';
      return 'secondary';
    },
    memoTooltip() {
      if (this.isMemoSent) return this.$t('CONVERSATION.MEMO.BUTTON_SENT_TOOLTIP');
      if (this.hasMemo) return this.$t('CONVERSATION.MEMO.BUTTON_PENDING_TOOLTIP');
      return this.$t('CONVERSATION.MEMO.BUTTON_TOOLTIP');
    },
  },
  mounted() {
    ActiveStorage.start();
    this.syncMemoState();
  },
  watch: {
    conversationId() {
      this.syncMemoState();
    },
    'currentChat.additional_attributes': {
      deep: true,
      handler(attrs) {
        const memo = attrs?.memo_message || '';
        const sent = attrs?.memo_sent === true;
        // If the backend cleared the memo (after sending), reset local state too
        if (!memo && !sent) {
          this.memoMessage = '';
          this.memoSent = false;
        } else {
          this.memoMessage = memo;
          this.memoSent = sent;
        }
      },
    },
    'currentChat.messages': {
      handler(messages, oldMessages) {
        if (!messages || !oldMessages) return;
        if (messages.length <= oldMessages.length) return;
        const newest = messages[messages.length - 1];
        if (newest?.message_type === 1 && newest?.content_attributes?.is_memo) {
          // Reset immediately when memo message arrives via ActionCable
          this.memoMessage = '';
          this.memoSent = false;
          bus.$emit('newToastMessage', this.$t('CONVERSATION.MEMO.SENT_AS_MESSAGE'));
        }
      },
    },
  },
  methods: {
    syncMemoState() {
      const attrs = this.currentChat?.additional_attributes || {};
      this.memoMessage = attrs.memo_message || '';
      this.memoSent = attrs.memo_sent === true;
    },
    toggleMemoModal() {
      this.showMemoModal = !this.showMemoModal;
    },
    onMemoSaved(text) {
      this.memoMessage = text;
      this.memoSent = false;
    },
    onMemoRemoved() {
      this.memoMessage = '';
      this.memoSent = false;
    },
    handleSendMessage(event) {
      this.handleModalCatalogProduct();
      this.$emit('send-message-product', event);
    },
    handleModalCatalogProduct() {
      this.showModalCatalogProduct = !this.showModalCatalogProduct;
    },
    inputFilter(newFile, oldFile, prevent) {
      if (newFile && !oldFile) {
        const fileType = newFile.type;
        const fileSize = newFile.size;

        let isValid = false;

        for (const mediaType in this.mediaRules) {
          const rules = this.mediaRules[mediaType];
          if (rules.types.includes(fileType) || rules.types.includes('*/*')) {
            if (fileSize <= rules.maxSize) {
              isValid = true;
              break;
            } else {
              rules.errorMessage(newFile.name, rules.maxSize);
              prevent();
              return;
            }
          }
        }

        if (!isValid) {
          bus.$emit(
            'newToastMessage',
            this.$t('CONVERSATION.REPLYBOX.FILE_UNSUPPORTED')
          );
          prevent();
        }
      }
    },
    onUpload(file) {
      this.onFileUpload(file);
      this.show = false;
    },
    onToggleContactsModal() {
      this.toggleContactsModal = !this.toggleContactsModal;
      this.show = false;
    },
    showModalNewConversation() {
      this.toggleContactsModal = true;
      this.show = false;
    },
    hideContactsModal() {
      this.toggleContactsModal = false;
    },
    selectLocation(addressData) {
      this.toggleLocation(addressData);
      this.showLocationModal = false;
    },
    resetInput() {
      this.address = '';
      this.$refs.address.clear();
    },
    handleKeyEvents(e) {
      if (hasPressedAltAndAKey(e)) {
        this.$refs.upload.$children[1].$el.click();
      }
    },
    toggleOptionsStatus() {
      this.show = !this.show;
    },
    toggleMessageSignature() {
      this.updateUISettings({
        send_with_signature: !this.sendWithSignature,
      });
    },
    toggleLocationModal() {
      this.show = false;
      this.showLocationModal = !this.showLocationModal;
    },
    replaceText(text) {
      this.$emit('replace-text', text);
    },
    onClickAway() {
      if (this.show) this.show = false;
    },
    handleInput() {
      this.showResults = true;
    },
    selectItem(item) {
      this.inputValue = item;
      this.showResults = false;
    },
    onClose() {
      this.showLocationModal = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.bottom-box {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--space-small) var(--space-normal);
  border-top: 1px solid var(--color-border);

  &.is-note-mode {
    background: var(--y-50);
  }
}

.left-wrap .button {
  margin-right: var(--space-small);
}

.left-wrap {
  align-items: center;
  display: flex;
}

.right-wrap {
  display: flex;
}

::v-deep .file-uploads {
  label {
    cursor: pointer;
  }
  &:hover .button {
    background: var(--s-100);
  }
}

.modal-mask {
  color: var(--s-600);
  background: var(--white-transparent);
  flex-direction: column;
}

.page-sub-title {
  color: var(--s-600);
}

.icon {
  font-size: 8rem;
}

.options-menu.dropdown-pane {
  left: var(--space-small);
  bottom: var(--space-larger);
  position: absolute;
  margin-right: unset;
  z-index: 100 !important;
  .button {
    margin-right: unset;
  }
}

.btn-user {
  background: var(--s-50) !important;
}

.btn-user .box {
  display: flex;
  align-items: center;
  gap: 3px;
}

.google-autocomplete {
  z-index: 9999;
}

.google-autocomplete-input {
  margin: auto;
  display: flex;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  margin: 20px 20px 0 20px;
}
.pac-container {
  z-index: 9999 !important;
}
</style>
